<template>
  <v-container>
    <v-btn @click="newListing">
      New Listing
    </v-btn>
    <v-layout column>
      <v-text-field v-model="searchTerm" label="Search" clearable />
      <v-flex v-for="listing in listings" :key="listing.id">
        <v-card>
          <v-layout row align-center>
            <v-flex xs-8 grow>
              <v-card-title>
                {{ listing.title }}
              </v-card-title>
            </v-flex>
            <v-flex xs-2 shrink>
              <v-btn @click="() => editListing(listing)">
                Edit
              </v-btn>
            </v-flex>
            <v-flex xs-2 shrink>
              <v-btn color="warning" @click="() => deleteListing(listing.id)">
                Delete
              </v-btn>
            </v-flex>
          </v-layout>
        </v-card>
      </v-flex>
    </v-layout>
    <v-dialog v-model="dialogOpen" max-width="800px">
      <v-card flat tile height="100%" class="pa-4">
        <v-card-title class="display-1 secondary--text">
          {{ listingAlreadyExists ? "Edit Listing" : "Add a New Listing" }}
        </v-card-title>
        <v-form
          ref="form"
          v-model="valid"
          lazy-validation
          @submit.prevent="submitListing"
        >
          <v-text-field
            v-model="currentListing.title"
            type="text"
            label="Name"
            :rules="requiredRule"
          />
          <v-textarea
            v-model="currentListing.description"
            label="Description"
            :rules="requiredRule"
          />
          <v-select
            v-model="currentListing.shields"
            multiple
            chips
            label="Shields"
            :items="shieldTitles"
          />
          <v-text-field
            v-model="currentListing.address"
            type="text"
            label="Address"
          />
          <v-text-field
            v-model="currentListing.phone"
            type="text"
            label="Phone Number"
          />
          <v-text-field
            v-model="currentListing.dsnphone"
            type="text"
            label="Military DSN Phone Number"
          />
          <v-text-field
            v-model="currentListing.email"
            type="text"
            label="Email Address"
          />
          <v-text-field
            v-model="currentListing.website"
            type="text"
            label="Website"
          />
          <v-checkbox
            v-model="currentListing.onbase"
            class="ma-0"
            color="primary"
            label="On Fort Meade"
          />
          <v-card-title class="display-1 secondary--text">
            Select Tags
          </v-card-title>
          <fma-lookahead
            label="Search for tags"
            :items="availableTags(services)"
            :on-search-submit="addTag"
          />
          <v-select
            v-for="service in services"
            :key="service.id"
            v-model="currentListing.tags"
            multiple
            chips
            :label="service.title"
            :items="sortedTags(service.tags).map(tag => tag.title)"
          />
          <v-spacer />
          <v-btn type="submit" :disabled="!validForm" color="primary">
            Submit
          </v-btn>
          <v-btn @click="dialogOpen = false">
            Cancel
          </v-btn>
        </v-form>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import _ from "lodash";
import { SHIELDS } from "@/constants.js";
import FmaLookahead from "@/components/FmaLookahead.vue";

export default {
  components: {
    FmaLookahead
  },
  data() {
    return {
      dialogOpen: false,
      listingAlreadyExists: false,
      currentListing: {},
      valid: true,
      requiredRule: [v => !!v || "Required"],
      searchTerm: ""
    };
  },
  computed: {
    // Returns either all of the listings or a filtered version of them if a search term exists
    listings() {
      // Create an alphabetically sorted list of listings
      let listings = _.sortBy(this.$store.state.listings, listing => {
        return _.toUpper(listing.title);
      });
      // We do a falsy check since we don't control how the clearable property in v-text-field works
      if (!this.searchTerm) {
        return listings;
      }
      return _.filter(listings, listing => {
        return _.includes(listing.title, this.searchTerm);
      });
    },
    shieldTitles() {
      return _.map(SHIELDS, value => value.title);
    },
    services() {
      return _.sortBy(this.$store.state.services, service => {
        return _.toUpper(service.title);
      });
    },
    /* validate the data before it goes out to the endpoint */
    validForm() {
      if (!this.dialogOpen) {
        return false;
      }
      // Compare the data to the previous version. It's not valid unless it's changed
      if (this.listingAlreadyExists) {
        let originalListing = _.find(this.listings, listing =>
          _.isEqual(listing.id, this.currentListing.id)
        );
        return !_.isEqual(originalListing, this.currentListing);
      }
      // Check for the title, description, shield(s) and tags when creating a new listing
      let { description, shields, tags, title } = this.currentListing;
      if (!title || !description || shields.length < 1 || tags.length < 1) {
        return false;
      }
      return true;
    }
  },
  mounted() {
    this.$store.dispatch("fetchListings");
    this.$store.dispatch("fetchServices");
  },
  methods: {
    // Adds a tag to the current listing based on the title
    addTag(tagTitle) {
      if (!_.includes(this.currentListing.tags, tagTitle)) {
        this.currentListing.tags.push(tagTitle);
      }
    },
    availableTags(services) {
      let tags = services.reduce((tagList, service) => {
        return _.union(tagList, this.sortedTags(service.tags));
      });
      //let tags = _.flatten();
      let reducedTags = _.difference(tags, this.currentListing.tags);
      return reducedTags;
    },
    // Launches the modal for creating a listing and clears out the currentListing value
    newListing() {
      this.listingAlreadyExists = false;
      this.currentListing = {
        onbase: false,
        shields: [],
        tags: []
      };
      this.dialogOpen = true;
    },
    // Launches the modal for a listing and copies it's data so that we don't overwrite it
    editListing(listing) {
      this.listingAlreadyExists = true;
      this.currentListing = _.cloneDeep(listing);
      this.dialogOpen = true;
    },
    // Deletes a listing by locating the ID in the local copy of the filtered listings
    // and then passing that listing object to the action in the store
    deleteListing(id) {
      const index = this.listings.findIndex(c => _.isEqual(c.id, id));
      if (confirm("Are you sure you want to delete this listing?")) {
        this.$store.dispatch("deleteListing", this.listings[index]);
      }
    },
    sortedTags(tags) {
      return _.sortBy(tags, tag => {
        return _.toUpper(tag.title);
      });
    },
    // Submit function which determines if the user is updating a listing or creating a new one and closes the dialog box
    submitListing() {
      if (this.listingAlreadyExists) {
        this.$store.dispatch("updateListing", this.currentListing);
      } else {
        this.$store.dispatch("addListing", this.currentListing);
      }
      this.dialogOpen = false;
    }
  }
};
</script>
